import React from 'react'

import { Formik } from 'formik'

import { Button, Form, Modal } from '_zari/core'

import { ZariContext } from '_zari/context'
import Yup from '_zari/validation/_yup'

import TiposDeDocumentoIdentificacionConstant from 'constants/tiposDeDocumentoIdentificacion'

import * as SociosCrud from 'services/socio.service'

import * as FormGroups from './formGroups'

const ValidationSchema = Yup.object().shape({
  tipoDocumento: Yup.string().required(),
  numeroDocumento: Yup.string()
    .required()
    .when('tipoDocumento', {
      is: TiposDeDocumentoIdentificacionConstant.DNI,
      then: Yup.string().min(8).max(8),
      otherwise: Yup.string().when('tipoDocumento', {
        is: TiposDeDocumentoIdentificacionConstant.RUC,
        then: Yup.string().min(11).max(11)
      })
    }),
  nombres: Yup.string().when('tipoDocumento', {
    is: TiposDeDocumentoIdentificacionConstant.DNI,
    then: Yup.string().min(3).max(100).required()
  }),
  apellidos: Yup.string().when('tipoDocumento', {
    is: TiposDeDocumentoIdentificacionConstant.DNI,
    then: Yup.string().min(3).max(100).required()
  }),
  razonSocial: Yup.string().when('tipoDocumento', {
    is: tipoDocumento => tipoDocumento !== TiposDeDocumentoIdentificacionConstant.DNI,
    then: Yup.string().min(3).max(200).required()
  }),
  direccion: Yup.string().min(5).max(200).notRequired(),
  email1: Yup.string().email().max(300).notRequired(),
  email2: Yup.string().email().max(300).notRequired()
})

const NuevoSocioModal = ({ handleClose, show, onCreate, defaultData, onUpdate, edicionData = {} }) => {
  const __zariContext = React.useContext(ZariContext)

  const formInitialValues = React.useMemo(() => {
    let numeroDocumento = ''
    if (defaultData && /^[0-9]{11}$/.test(defaultData.numeroDocumento)) {
      numeroDocumento = defaultData.numeroDocumento
    }

    return {
      tipoDocumento: edicionData.tipoDocumento || TiposDeDocumentoIdentificacionConstant.RUC,
      numeroDocumento: edicionData.numeroDocumento || numeroDocumento,
      nombres: edicionData.nombres || '',
      apellidos: edicionData.apellidos || '',
      razonSocial: edicionData.razonSocial || '',
      sobrenombre: edicionData.sobrenombre || '',
      direccion: edicionData.direccion || '',
      email1: edicionData.email1 || '',
      email2: edicionData.email2 || '',
      _idDepartamento: edicionData._idDepartamento || '',
      _idProvincia: edicionData._idProvincia || '',
      _idDistrito: edicionData._idDistrito || ''
    }
  }, [defaultData, edicionData])

  const submitHandler = async nuevoSocioData => {
    if (edicionData) {
      nuevoSocioData._id = edicionData._id
    }

    SociosCrud.nuevoSocioViaVentas(nuevoSocioData)
      .then(({ data }) => {
        __zariContext.snack('success', 'El registros se ha completado.')
        // codigo: "0000000017"
        // facturacionConIgv: true
        // numeroDocumento: "20553856451"
        // razonSocial: "BI GRAND CONFECCIONES S.A.C."
        // tipoDocumento: "6"
        // _id: "2323330a-a5ad-4aa9-946f-44430536296b"
        if (edicionData && edicionData._id) {
          onUpdate({
            razonSocial: nuevoSocioData.razonSocial
          })
        } else {
          onCreate(data)
        }
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header>
        <Modal.Title>{edicionData._id ? 'ACTUALIZAR CLIENTE' : 'NUEVO CLIENTE'} </Modal.Title>
      </Modal.Header>
      <Formik initialValues={formInitialValues} onSubmit={submitHandler} validationSchema={ValidationSchema}>
        {({ errors, handleSubmit, isSubmitting, touched, values, handleChange, setValues }) => (
          <Form>
            <Modal.Body>
              <FormGroups.TipoDocumentoFormGroup
                iError={errors.tipoDocumento}
                iTouched={touched.tipoDocumento}
                fIsSubmitting={!!edicionData._id || isSubmitting}
              />
              <FormGroups.NumeroDocumentoFormGroup
                handleChange={handleChange}
                setValues={setValues}
                values={values}
                iError={errors.numeroDocumento}
                iTouched={touched.numeroDocumento}
                vId={edicionData._id}
                fIsSubmitting={!!edicionData._id || isSubmitting}
              />
              {values.tipoDocumento === TiposDeDocumentoIdentificacionConstant.DNI ? (
                <>
                  <FormGroups.NombresFormGroup iError={errors.nombres} iTouched={touched.nombres} fIsSubmitting={isSubmitting} />
                  <FormGroups.ApellidosFormGroup iError={errors.apellidos} iTouched={touched.apellidos} fIsSubmitting={isSubmitting} />
                </>
              ) : (
                <FormGroups.RazonSocialFormGroup iError={errors.razonSocial} iTouched={touched.razonSocial} fIsSubmitting={isSubmitting} />
              )}
              <FormGroups.SobrenombreFormGroup iError={errors.sobrenombre} iTouched={touched.sobrenombre} fIsSubmitting={isSubmitting} />
              <FormGroups.DireccionFormGroup iError={errors.direccion} iTouched={touched.direccion} fIsSubmitting={isSubmitting} />
              <FormGroups.DepartamentoFormGroup
                values={values}
                setValues={setValues}
                handleChange={handleChange}
                iError={errors._idDepartamento}
                iTouched={touched._idDepartamento}
                fIsSubmitting={isSubmitting}
              />
              <FormGroups.ProvinciaFormGroup
                values={values}
                setValues={setValues}
                handleChange={handleChange}
                idDepartamento={values._idDepartamento}
                iError={errors._idProvincia}
                iTouched={touched._idProvincia}
                fIsSubmitting={isSubmitting}
              />
              <FormGroups.DistritoFormGroup
                idProvincia={values._idProvincia}
                iError={errors._idDistrito}
                iTouched={touched._idDistrito}
                fIsSubmitting={isSubmitting}
              />
              <FormGroups.Email1FormGroup iError={errors.email1} iTouched={touched.email1} fIsSubmitting={isSubmitting} />
              <FormGroups.Email2FormGroup iError={errors.email2} iTouched={touched.email2} fIsSubmitting={isSubmitting} />
            </Modal.Body>
            <Modal.Footer>
              <Button color='secondary' onClick={handleClose}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}> {edicionData._id ? 'Actualizar' : 'Crear'}</Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default React.memo(NuevoSocioModal)
